import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { NctsCountry } from '../../../../config'
import { tryCatchAsync } from '../../../../helpers/try-catch'
import { useConfirm } from '../../../../shared/components/confirm/ConfirmProvider'
import { transitOperationLogsQueryOptions } from '../../event-log/api/fetchTransitOperationLogs'
import { isEnsAssessmentComplete, readMessageLog } from '../../event-log/models/messageLogSignals'

function useEnsAssessmentWarning(transitOperationId: number | null, country: NctsCountry | null) {
  const { t } = useTranslation()
  const { confirm } = useConfirm()
  const queryClient = useQueryClient()

  const fetchLatestLogs = useCallback(async () => {
    if (!transitOperationId) {
      return null
    }

    const { data } = await tryCatchAsync(() => queryClient.fetchQuery({
      ...transitOperationLogsQueryOptions(transitOperationId),
      retry: false,
    }))

    return data
  }, [queryClient, transitOperationId])

  const confirmEnsPresentationNotification = useCallback(async (): Promise<boolean> => {
    if (country !== 'LT') {
      return true
    }

    const logs = await fetchLatestLogs()
    if (logs && isEnsAssessmentComplete(readMessageLog(logs, country))) {
      return true
    }

    return confirm({
      title: t('transitOperation.ensPresentationNotification.assessmentWarning.title'),
      message: t('transitOperation.ensPresentationNotification.assessmentWarning.message'),
      confirmText: t('transitOperation.ensPresentationNotification.assessmentWarning.confirm'),
      cancelText: t('transitOperation.ensPresentationNotification.assessmentWarning.cancel'),
      danger: true,
    })
  }, [confirm, country, fetchLatestLogs, t])

  return { confirmEnsPresentationNotification }
}

export default useEnsAssessmentWarning
